/**
 * ErrorBoundary - Composant de gestion d'erreurs React
 * Capture les erreurs JavaScript dans l'arbre des composants
 * Version moderne avec Material-UI v7
 */
import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';

/**
 * Composant d'affichage d'erreur avec détails techniques
 * @param {Object} props - Props du composant
 * @param {Error} props.error - Objet erreur capturée
 * @param {string} props.errorInfo - Informations sur l'erreur
 * @param {Function} props.onReset - Fonction de réinitialisation
 * @param {Function} props.onGoHome - Fonction de navigation vers l'accueil
 * @returns {JSX.Element} Interface d'erreur
 */
const ErrorDisplay = ({ error, errorInfo, onReset, onGoHome }) => (
  <Container
    maxWidth="md"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '80vh',
      py: 4,
    }}
  >
    <Paper
      elevation={6}
      sx={{
        p: 4,
        borderRadius: 3,
        width: '100%',
        background: 'linear-gradient(145deg, #ffebee 0%, #ffffff 100%)',
        border: '1px solid #ffcdd2',
      }}
    >
      {/* Icône et titre */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BugReportIcon sx={{ fontSize: '3rem', color: 'error.main' }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              color: 'error.main',
              mb: 1,
            }}
          >
            Erreur de l'application
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'text.secondary',
            }}
          >
            Une erreur inattendue s'est produite
          </Typography>
        </Box>
      </Stack>

      {/* Message principal */}
      <Typography
        variant="body1"
        sx={{
          color: 'text.primary',
          mb: 4,
          lineHeight: 1.6,
        }}
      >
        Nous nous excusons pour ce problème. L'application a rencontré une erreur
        et ne peut pas continuer normalement. Vous pouvez essayer de recharger la page
        ou retourner à l'accueil.
      </Typography>

      {/* Boutons d'action */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="center"
        sx={{ mb: 4 }}
      >
        <Button
          variant="contained"
          size="large"
          startIcon={<RefreshIcon />}
          onClick={onReset}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
          }}
        >
          Recharger la page
        </Button>

        <Button
          variant="outlined"
          size="large"
          startIcon={<HomeIcon />}
          onClick={onGoHome}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
          }}
        >
          Retour à l'accueil
        </Button>
      </Stack>

      {/* Détails techniques (développement seulement) */}
      {import.meta.env.DEV && error && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="error-details-content"
            id="error-details-header"
            sx={{ backgroundColor: '#f5f5f5' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Détails techniques (développement)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Message d'erreur :
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    backgroundColor: '#f8f8f8',
                    p: 1,
                    borderRadius: 1,
                    color: 'error.main',
                  }}
                >
                  {error.message}
                </Typography>
              </Box>

              {error.stack && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Stack trace :
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f8f8',
                      p: 1,
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {error.stack}
                  </Typography>
                </Box>
              )}

              {errorInfo && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Informations du composant :
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f8f8f8',
                      p: 1,
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem',
                      maxHeight: '200px',
                      overflow: 'auto',
                    }}
                  >
                    {errorInfo}
                  </Typography>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  </Container>
);

/**
 * ErrorBoundary Class Component
 * Capture les erreurs JavaScript dans l'arbre des composants enfants
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Méthode statique appelée quand une erreur est capturée
   * @param {Error} error - Erreur capturée
   * @returns {Object} Nouvel état du composant
   */
  static getDerivedStateFromError(error) {
    // Update state to show error UI on next render
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Méthode appelée quand une erreur est capturée
   * @param {Error} error - Erreur capturée
   * @param {Object} errorInfo - Informations sur l'erreur
   */
  componentDidCatch(error, errorInfo) {
    // Log de l'erreur pour le développement seulement
    if (import.meta.env.DEV) {
      // En développement, log dans la console pour débugger
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    this.setState({
      error,
      errorInfo: errorInfo.componentStack,
    });

    // Ici on pourrait envoyer l'erreur à un service de monitoring
    // comme Sentry, LogRocket, etc.
    if (import.meta.env.PROD) {
      // Example: logErrorToService(error, errorInfo);
    }
  }

  /**
   * Réinitialise l'état d'erreur pour permettre un nouveau rendu
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Navigate to home page
   */
  handleGoHome = () => {
    // Reset state then navigate
    this.handleReset();
    globalThis.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // UI d'erreur personnalisée
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onGoHome={this.handleGoHome}
        />
      );
    }

    // Rendu normal des enfants
    return this.props.children;
  }
}

ErrorBoundary.displayName = 'ErrorBoundary';

export default ErrorBoundary;
